import React, { useEffect, useState } from 'react'
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonListHeader,
  IonLabel,
  IonList,
  IonFooter,
  IonButtons,
  IonButton,
  IonLoading,
  IonCol,
  IonRow,
  IonItem,
  IonToast,
} from '@ionic/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { IZollSupportTicketState } from '../store/zollSupportTicket.types'
import { getZollSupportTicketState } from '../selectors'
import {
  createZollSupportTicket,
  setEmailAddress,
  setIsLoading,
  setPhoneNumber,
  setPreferredContactMethod,
  setQuestionOrProblem,
  setTicketId,
} from '../store/zollSupportTicket.actions'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import { isValidPhoneNumber, transformPhoneNumber } from '../../../utilities/phoneNumber.util'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { emailOption, preferredContactMethodOptions } from '../store/zollSupportTicket.reducers'
import { IOption } from '../../../common/common.types'
import { ZollSupportTicketConstants } from '../store/zollSupportTicket.constants'
import Validation from '../../../components/validation/Validation.component'
import { isValidEmailAddress } from '../../../utilities/emailAddress.util'
import { getApiErrorHandlerSelector } from '../../../components/apiErrorHandler/selectors/getApiErrorHandler.selector'
import { getUserSelector } from '../../user/selectors'
import TextArea from '../../../components/textArea/TextArea.component'
import SInput from '../../../components/stringInput/StringInput.component'
import { ToastColor } from '../../../components/toast/store/toast.constants'
import { getToastProps } from '../../../components/toast/toastIcon.util'

export interface IZollSupportTicketModalProps {
  showModal: boolean
  resetModal: () => void
}

const ZollSupportTicketModal: React.FC<IZollSupportTicketModalProps> = (props: IZollSupportTicketModalProps) => {
  const { showModal, resetModal } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const ZollSupportTicketState: IZollSupportTicketState = useSelector(getZollSupportTicketState)
  const { isLoading, emailAddress, phoneNumber, preferredContactMethod, questionOrProblem, ticketId } =
    ZollSupportTicketState
  const apiErrorHandler = useSelector(getApiErrorHandlerSelector)
  const { error } = apiErrorHandler
  const userState = useSelector(getUserSelector)
  const { email } = userState
  const [showToast, setShowToast] = useState(false)

  useEffect(() => {
    if (showModal) {
      dispatch(setTicketId(null))
    }
  }, [showModal])

  useEffect(() => {
    if (ticketId) {
      setShowToast(true)
      dismiss()
    }
  }, [ticketId])

  useEffect(() => {
    if (showModal && error?.status === 401) dismiss()
  }, [error])

  const initializeModal = () => {
    dispatch(setEmailAddress(email))
  }

  const cancel = () => {
    dismiss()
  }

  const dismiss = () => {
    resetModal()
    dispatch(setEmailAddress(''))
    dispatch(setQuestionOrProblem(''))
    dispatch(setPhoneNumber(''))
    dispatch(setPreferredContactMethod(emailOption))
  }

  const handleSubmit = () => {
    dispatch(setIsLoading(true))
    dispatch(createZollSupportTicket())
  }

  const setEmailAddresseOnChange = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setEmailAddress(e.detail.value))
  }

  const setPhoneNumberOnChange = (numbers: string[]) => {
    const newPhoneNumber = transformPhoneNumber(numbers.join(''))
    dispatch(setPhoneNumber(newPhoneNumber))
  }

  const setpreferredContactMethodOnChange = (option: IOption) => {
    dispatch(setPreferredContactMethod(option))
  }

  const setQuestionOrProblemOnChange = (e: CustomEvent) => {
    dispatch(setQuestionOrProblem(e.detail.value))
  }

  const hasRequiredData = () => {
    let contact = ''
    const isValidEmail = emailAddress ? isValidEmailAddress(emailAddress) : true
    const isValidPhone = phoneNumber ? isValidPhoneNumber(phoneNumber) : true
    if (preferredContactMethod?.name === 'Email') {
      contact = emailAddress
    }
    if (preferredContactMethod?.name === 'Phone Number') {
      contact = phoneNumber
    }
    return (
      questionOrProblem !== null &&
      questionOrProblem.trim() !== '' &&
      contact !== null &&
      contact.trim() !== '' &&
      isValidEmail &&
      isValidPhone
    )
  }

  return (
    <>
      {!isLoading && ticketId && (
        <IonToast
          isOpen={showToast}
          position='top'
          message={t('zollSupportTicket.ZOLLSupportTicketSuccess') + ticketId}
          {...getToastProps(ToastColor.Success)}
          onDidDismiss={() => {
            setShowToast(false)
            setTicketId(null)
            dismiss()
          }}
        />
      )}
      <IonModal
        id='ZollSupportTicketModal'
        data-testid='ZollSupportTicketModal'
        showBackdrop={showModal}
        isOpen={showModal}
        onDidPresent={initializeModal}
        backdropDismiss={false}
        onDidDismiss={dismiss}
        className='multi-select-modal'>
        <IonHeader className='ion-no-border'>
          <IonToolbar color='light'>
            <IonListHeader color='light'>
              <IonLabel>{t('zollSupportTicket.CreateZOLLSupportTicket')}</IonLabel>
            </IonListHeader>
          </IonToolbar>
        </IonHeader>
        <IonList>
          <IonItem lines='none'>
            <IonLabel className='ion-text-wrap' color='medium'>
              <p>{t('zollSupportTicket.ZOLLSupportTicketSubHeader')}</p>
            </IonLabel>
          </IonItem>

          <IonRow>
            <IonCol className='picklist'>
              <SInput
                label={t('zollSupportTicket.EmailAddress')}
                dataTestID='Address'
                inputValue={emailAddress}
                onChangeAction={setEmailAddresseOnChange}
              />

              <Validation id={ZollSupportTicketConstants.SET_EMAIL_ADDRESS} showReduxStateErrorsOnlyFlag={true} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <NumberPad
                elementId={'PhoneNumber'}
                dataTestId={'PhoneNumber'}
                values={phoneNumber.split('')}
                label={t('zollSupportTicket.PhoneNumber')}
                onNumPadClick={(numbers) => {
                  setPhoneNumberOnChange(numbers)
                }}
              />
              <Validation id={ZollSupportTicketConstants.SET_PHONE_NUMBER} showReduxStateErrorsOnlyFlag={true} />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <SingleSelect
                dataTestID='PreferredContactMethod'
                options={preferredContactMethodOptions}
                selectedOption={preferredContactMethod}
                listHeader={t('zollSupportTicket.PreferredContactMethod')}
                label={t('zollSupportTicket.PreferredContactMethod')}
                action={setpreferredContactMethodOnChange}
              />
            </IonCol>
          </IonRow>

          <hr className='field-divider' />

          <IonRow>
            <IonCol className='picklist'>
              <TextArea
                dataTestID='QuestionOrProblem'
                label={t('zollSupportTicket.QuestionOrProblem')}
                rows={3}
                maxlength={10000}
                value={questionOrProblem}
                validationId={''}
                onChangeAction={(e) => setQuestionOrProblemOnChange(e)}
              />
            </IonCol>
          </IonRow>
          <hr className='field-divider' />
        </IonList>
        <IonFooter className='ion-no-border'>
          <IonToolbar color='light' className='p-10'>
            <IonButtons>
              <IonButton
                data-testid='Cancel'
                className='btn btn-secondary mr-8'
                expand='block'
                onClick={() => cancel()}>
                {t('common.buttons.Cancel')}
              </IonButton>
              <IonButton
                disabled={!hasRequiredData()}
                data-testid='Submit'
                className='btn btn-primary'
                expand='block'
                onClick={() => handleSubmit()}>
                {t('common.buttons.Submit')}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
        {isLoading && <IonLoading data-testid='Loader' isOpen animated showBackdrop spinner={'crescent'} />}
      </IonModal>
    </>
  )
}

export default ZollSupportTicketModal
